// Copyright 2019 Drivetrain Hub LLC
// vue-apollo plugin configuration
//
// Apollo References:
//  1. https://vue-cli-plugin-apollo.netlify.com/guide/configuration.html
//  2. https://vue-apollo.netlify.com/api/apollo-provider.html#constructor
//  3. https://github.com/Akryum/vue-apollo/issues/460
//  4. https://www.apollographql.com/docs/react/recipes/authentication/
//  5. https://www.apollographql.com/docs/react/advanced/fragments/#fragment-matcher
//
// Other References:
//  1. https://jasonwatmore.com/post/2018/07/06/vue-vuex-jwt-authentication-tutorial-example
//  2. https://stackoverflow.com/questions/5639346/what-is-the-shortest-function-for-reading-a-cookie-by-name-in-javascript
//  3. https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie
//  4. https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework

import Vue from "vue";
import VueApollo from "vue-apollo";
import { createApolloClient } from "vue-cli-plugin-apollo/graphql-client";
import { ApolloLink } from "apollo-link";
import { createHttpLink } from "apollo-link-http";
import { onError } from "apollo-link-error";
import { IntrospectionFragmentMatcher } from "apollo-cache-inmemory";
import introspectionQueryResultData from "./apolloFragmentTypes.json";
import { displayRoute, router } from "./router";
import requests from "../requests";
import handlers from "../handlers";
import { store } from "../store";
import { Message } from "element-ui"

// install plugin
Vue.use(VueApollo);

// region HELPERS

function getCookieValue(name) {
  let val = document.cookie.match('(^|[^;]+)\\s*' + name + '\\s*=\\s*([^;]+)');
  return val ? val.pop() : '';
}

export const CSRF_TOKEN = getCookieValue('csrf_access_token');  // cookie name per dhw-site

const PART_TYPENAMES = ['Part', ...Object.values(requests.model.TYPENAME.PART)];

function getApolloId({__typename, Id}) {
  // For use with Apollo config of dataIdFromObject and cache functions.

  if (PART_TYPENAMES.includes(__typename)) {
    return 'Part' + Id;
  } else {
    return !Id ? null : __typename + Id;
  }
}

// endregion

// region CONFIG

// region LINK

// HTTP LINKS

const httpEndpoint = String(process.env.VUE_APP_API_GEARS);
const httpEndpointSite = String(process.env.VUE_APP_API_SITE);

const linkOptions = {
  headers: {'X-CSRF-TOKEN': CSRF_TOKEN},  // CSRF token header required with server-side JWT config
  credentials: 'include',  // send cookies to all origins; JWT is in cookie
  // credentials: 'same-origin',  //  send cookies only to this app's host and port
};
const httpLink = createHttpLink({
  ...linkOptions,
  uri: httpEndpoint,
});
const httpLinkSite = createHttpLink({
  ...linkOptions,
  uri: httpEndpointSite,
});

// ERROR LINKS

const PREFIX_422_LEN = '422 Unprocessable Entity: '.length;
const onErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // IMPORTANT: The Apollo settings for errorPolicy do not avoid throwing an error here, hence the operation condition.

  // DEV ONLY
  // console.log('graphQLErrors', graphQLErrors);
  // console.log('networkError', networkError);
  // console.log('response', response);
  // console.log('operation', operation);
  // console.log('operation.getContext', operation.getContext());

  // ignore certain GraphQL operations that are expected to error
  if (operation.operationName === 'computeToothRoot') return;

  // log GraphQL errors
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) => {
      // eslint-disable-next-line no-console
      console.log(
        '%cAPI Error',
        'background: deeppink; color: black; padding: 2px 4px; border-radius: 3px; font-weight: bold;',
        `Message: ${message}, Location: ${locations}, Path: ${path}`
      );

      // handle based on message
      if (!networkError) {
        // display all API error messages that do have a network error
        let showMsg = true;
        let displayMsg = message;

        if (message.startsWith('422 Unprocessable Entity')) {
          displayMsg = message.slice(PREFIX_422_LEN)
        } else if (message.includes('Cannot delete')) {
          displayMsg = 'Cannot delete while being referenced by other components.';
        } else if (message.startsWith('404 Not Found') || message.includes('not a valid ObjectId')) {
          // not found, display error page
          displayRoute({name: 'notFound'});
          showMsg = false;
        }
        // OLD: else if (message.includes('Tool tip'))

        if (showMsg) Message({type: 'error', center: true, message: displayMsg});
      }
    });
  }

  // handle network errors
  if (networkError) {
    // eslint-disable-next-line no-console
    console.log('%cNetwork Error', 'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;', networkError.message);

    // handler error based on HTTP status code
    switch (networkError.statusCode) {
      case 400:
        // bad request, display error page
        displayRoute({name: 'badRequest'});
        break;
      case 401:
        // unauthenticated, redirect to signin
        store.commit('SET_SKIP_ROUTE_CONFIRM', true);
        router.push({name: 'signin'});
        break;
      case 403:
        // unauthorized, display error page
        displayRoute({name: 'forbidden'});
        break;
      case 404:
        // not found, display error page
        displayRoute({name: 'notFound'});
        break;
      default:
        Message({type: 'error', center: true, message: 'Unexpected error.  Please contact support.'});
    }

    // propagate error
    throw new Error(networkError.message);
  }
});

// LINKS

const link = ApolloLink.from([onErrorLink, httpLink]);
const linkSite = ApolloLink.from([onErrorLink, httpLinkSite]);

// endregion

// region FRAGMENT MATCHER
// Required when schema contains Interface or Union type.
// PLACEHOLDER {automate} automate the process of running fragment-matcher
// IMPORTANT: If schema fragment types change, follow these steps:
//  1. Download latest schema.graphql via Altair plugin in Firefox.
//  2. `npm run fragment-matcher` to rebuild apolloFragmentTypes.json.

const fragmentMatcher = new IntrospectionFragmentMatcher({ introspectionQueryResultData });

// endregion

// Apollo Client options
const defaultOptions = {
  // URL to the HTTP API; see link instead
  // httpEndpoint,
  // URL to the Websocket API
  wsEndpoint: null,
  // Token used in localStorage
  tokenName: null,
  // Enable this if you use Query persisting with Apollo Engine
  persisting: false,
  // Is being rendered on the server?
  ssr: false,
  // Only use Websocket for all requests (including queries and mutations)
  websocketsOnly: false,
  // Custom starting link. To replace the default HttpLink, set `defaultHttpLink` to false.
  link: link,
  // If true, the default HttpLink is used.  If false, replace it with a terminating link using `link` option.
  defaultHttpLink: false,
  // Options for the default HttpLink; https://www.apollographql.com/docs/react/recipes/authentication/#cookie
  httpLinkOptions: {},
  // Custom Apollo cache implementation (default is apollo-cache-inmemory)
  cache: null,
  // Options for the default cache
  inMemoryCacheOptions: {
    // https://www.apollographql.com/docs/react/advanced/caching/#configuration
    // WARNING: Apollo cache handles embedded documents (without Id) the same as top-level documents.
    // Embedded documents are ignored by checking that object.Id exists.
    dataIdFromObject: object => getApolloId(object),
    fragmentMatcher: fragmentMatcher,
    cacheRedirects: {
      Query: {
        connection: (_, args, { getCacheKey }) =>
          getCacheKey({ __typename: 'Connection', Id: args.Id })
      },
    },
  },
  // Additional Apollo client options
  apollo: {},
  // apollo-link-state options
  clientState: null,
  // Function returning Authorization header token; if using cookies, return undefined.
  // https://www.apollographql.com/docs/react/recipes/authentication/#header
  getAuth: () => {return undefined},
};
const siteOptions = {
  ...defaultOptions,
  // overwrite default options
  link: linkSite,
  inMemoryCacheOptions: {},
};

function createProvider(options = {}) {
  // create default apollo client
  const {apolloClient} = createApolloClient({
    ...defaultOptions,
    ...options,
  });

  // create site apollo client
  const {apolloClient: apolloClientSite} = createApolloClient({
    ...siteOptions,
    ...options,
  });

  return new VueApollo({
    clients: {
      site: apolloClientSite,
    },
    defaultClient: apolloClient,
    defaultOptions: {
      // $query: {
      //   fetchPolicy: 'cache-and-network',
      //   errorPolicy: 'ignore',
      // },
    },
  })
}

// use this in the Vue app file
export const apolloProvider = createProvider();

// endregion

// region FUNCTIONS
// Use the following with for non-smart queries and mutations, e.g. with Vuex actions or component methods.
// These functions are defined because the Apollo global error handler is only used with a SmartQuery.

export function apolloFieldsString(arr) {
  // Convert an array of field names to a string compatible with GraphQL queries and mutations.

  let fieldsString = '';
  arr.forEach(key => {
    fieldsString += key + '\n ';
  });

  return fieldsString
}

export async function apolloQuery(query) {
  return await apolloProvider.defaultClient.query(query);
}

export async function apolloMutate(mutate) {
  return await apolloProvider.defaultClient.mutate(mutate);
}

// Manually call this when user log in
// export async function onLogin(apolloClient, token) {
//   if (typeof localStorage !== 'undefined' && token) {
//     localStorage.setItem(AUTH_TOKEN, token)
//   }
//   if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
//   try {
//     await apolloClient.resetStore()
//   } catch (e) {
//     // eslint-disable-next-line no-console
//     console.log('%cError on cache reset (login)', 'color: orange;', e.message)
//   }
// }

// Manually call this when user log out
// export async function onLogout(apolloClient) {
//   if (typeof localStorage !== 'undefined') {
//     localStorage.removeItem(AUTH_TOKEN)
//   }
//   if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
//   try {
//     await apolloClient.resetStore()
//   } catch (e) {
//     // eslint-disable-next-line no-console
//     console.log('%cError on cache reset (logout)', 'color: orange;', e.message)
//   }
// }

// endregion

// region CACHE FUNCTIONS

function readQuery(options) {
  // Read query from Apollo cache.
  // https://www.apollographql.com/docs/react/api/apollo-client/#ApolloClient.readQuery
  // https://www.apollographql.com/docs/react/features/caching/

  return apolloProvider.defaultClient.readQuery(options);
}

function writeQuery(options) {
  // Write query from Apollo cache.
  // https://www.apollographql.com/docs/react/api/apollo-client/#ApolloClient.writeQuery
  // https://www.apollographql.com/docs/react/features/caching/

  return apolloProvider.defaultClient.writeQuery(options);
}

function readFragment(__typename, Id, fragment) {
  // Read fragment from Apollo cache.
  // https://www.apollographql.com/docs/react/api/apollo-client/#ApolloClient.readFragment
  // https://www.apollographql.com/docs/react/features/caching/

  // Notes:
  //  1. Intentionally uses lower case `id`.
  //  2. `id` must match format of dataIdFromObject.

  return apolloProvider.defaultClient.readFragment({
    id: getApolloId({__typename, Id}),
    fragment: fragment,
  });
}

function writeFragment(__typename, Id, fragment, data) {
  // Write fragment to Apollo cache.
  // WARNING: Due to how cache updates work, this does not broadcast cache changes for items in an array, like a Part.
  // https://www.apollographql.com/docs/react/caching/cache-interaction/#writequery-and-writefragment
  // https://www.apollographql.com/docs/react/features/caching/

  apolloProvider.defaultClient.writeFragment({
    id: getApolloId({__typename, Id}),
    fragment: fragment,
    data: data,  // must include __typename
  });
}

export const apolloStore = {
  readQuery: readQuery,
  writeQuery: writeQuery,
  readFragment: readFragment,
  writeFragment: writeFragment,
  getApolloId: getApolloId,
};

// endregion

// region $gql
// Plugin designed for special use cases that require middleware.
// All functions are made available to app instance via $gql.
// https://vuejs.org/v2/guide/plugins.html

// Cannot type hint PluginObject without losing variable resolution.
// eslint-disable-next-line no-unused-vars
import { PluginFunction } from "vue";
import {copyDeep, copyDeepJSON} from "../assets/js/utils";

/**
 * Convert units by mutating values based on object typename.
 * @param item {Object} - Object to mutate.
 * @param toUnits {Object} - Units to convert to.
 * @return {Object} Mutated input object.
 */
function convertUnits(item, toUnits) {
  switch (item.__typename) {
    case requests.gear.TYPENAME.GEAR_DETAIL:
    case requests.gear.TYPENAME.GEAR_CONCEPT:
    case requests.gear.TYPENAME.GEAR_PHASING:
      return handlers.gear.convertGearGeom(item, undefined, toUnits);
    case requests.tool.TYPENAME.HOB:
      return handlers.tool.convertHobGeom(item, undefined, toUnits);
    case requests.tool.TYPENAME.PINION_CUTTER:
      return handlers.tool.convertPinionCutterGeom(item, undefined, toUnits);
    case requests.tool.TYPENAME.BASIC_RACK:
      return handlers.tool.convertBasicRackGeom(item, undefined, toUnits);
    default: {
      // notify user and throw error to prevent displaying incorrect units
      Message({type: 'error', center: true, message: 'Query units error.  Please contact support.'});
      throw Error('Could not identify typename for units conversion.');
    }
  }
}

async function queryConvertUnits(query, toUnits) {
  // Query a single object and apply unit conversions based on __typename.
  // https://stackoverflow.com/questions/983267/how-to-access-the-first-property-of-an-object-in-javascript

  const response = await apolloProvider.defaultClient.query(query);

  // get response object; response.data.<queriedObject>
  // deep copy avoids mutating Apollo cache in unit conversion
  let item = copyDeep(Object.values(response.data)[0]);

  // DEV ONLY
  // convertGeom(item, toUnits);
  // console.log('item', item);
  // console.log('cache', readQuery(query));

  // mutate values to convert units
  return convertUnits(item, toUnits);
}

async function queryConvertUnitsArr(query, toUnits) {
  // Query a list of objects with geom and apply unit conversions based on geom __typename.
  // https://stackoverflow.com/questions/983267/how-to-access-the-first-property-of-an-object-in-javascript

  const response = await apolloProvider.defaultClient.query(query);

  // get response object; response.data.<queriedObject>
  // deep copy avoids mutating Apollo cache in unit conversion
  let arr = copyDeepJSON(Object.values(response.data)[0]);

  // return array with converted units
  return arr.map(item => convertUnits(item, toUnits));
}

async function mutateConvertUnits(mutate, fromUnits) {
  // Assumes a single mutation variable, `data`.

  // get mutation input object, e.g. BasicRackInput object
  let obj = mutate.variables.data;

  // get arbitrary name of mutation, e.g. 'createBasicRack'
  // const mutationName = mutate.mutation.definitions[0].name.value.toLowerCase();

  // get name of mutation input type, e.g. 'BasicRackInput'
  const mutationInput = mutate.mutation.definitions[0].variableDefinitions[0].type.type.name.value;

  // convert units based on mutation input type
  switch (mutationInput) {
    case requests.gear.INPUT_TYPE.CREATE_GEAR_DETAIL:
    case requests.gear.INPUT_TYPE.UPDATE_GEAR_DETAIL:
      handlers.gear.convertGearGeom(obj, fromUnits, undefined);
      break;
    case requests.tool.INPUT_TYPE.CREATE_HOB:
    case requests.tool.INPUT_TYPE.UPDATE_HOB:
      handlers.tool.convertHobGeom(obj, fromUnits, undefined);
      break;
    case requests.tool.INPUT_TYPE.CREATE_PINION_CUTTER:
    case requests.tool.INPUT_TYPE.UPDATE_PINION_CUTTER:
      handlers.tool.convertPinionCutterGeom(obj, fromUnits, undefined);
      break;
    case requests.tool.INPUT_TYPE.CREATE_BASIC_RACK:
    case requests.tool.INPUT_TYPE.UPDATE_BASIC_RACK:
      handlers.tool.convertBasicRackGeom(obj, fromUnits, undefined);
      break;
    default: {
      // notify user and throw error to prevent storing incorrect units
      Message({type: 'error', center: true, message: 'Mutation input error.  Please contact support.'});
      throw Error('Could not identify mutation input type for units conversion.');
    }
  }

  // DEV ONLY
  // console.log('MUTATED UNITS', item);

  // GraphQL mutation
  const response = await apolloProvider.defaultClient.mutate(mutate);

  // return mutation response object; response.data.<mutationObject>
  return Object.values(response.data)[0];
}

/** @alias $gql */
export const graphql = {
  queryConvertUnits: queryConvertUnits,
  queryConvertUnitsArr: queryConvertUnitsArr,
  mutateConvertUnits: mutateConvertUnits,
};

/** @type {PluginFunction} */
graphql.install = function(Vue) {
  Object.defineProperty(Vue.prototype, "$gql", {
    get () { return graphql }
  })
};

// endregion
